import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TaggingOptions } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  AssetsGenerateUrlService,
  BuyoutRightsDialogs,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  StepperGroupModel,
  StepperModel,
  TableColumn,
} from '@thehubz/client/core';
import {
  Asset,
  AssetsApiService,
  AssetsBulkEditApiService,
  BuyoutRights,
  MakeBuyoutRights,
  UpdateAssetManyDto,
  UsageRights,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, BuyoutRightsMultiEditPayload } from '@thehubz/shared/model';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetUploadService } from '../../services/asset-upload.service';
import { BuyoutRightsColumnData } from '../asset-detail-view/asset-usage-rights/table-column-data';
import { AssetItemBasicComponent } from '../asset-item-basic/asset-item-basic.component';
import { AssetSettingsStepperComponent } from '../asset-settings-stepper/asset-settings-stepper.component';
import { AssetBuyoutRightsStepperConfigService } from '../asset-upload/stepper-config/asset-buyout-rights-stepper-config.service';
import { BuyoutRightsConfig, getBuyoutRightsConfig } from '../asset-upload/stepper-config/asset-settings-stepper.constants';
import { BuyoutRightsCustomComponent } from './buyout-rights-custom/buyout-rights-custom.component';
import { BuyoutRightsStandardComponent } from './buyout-rights-standard/buyout-rights-standard.component';

@Component({
  selector: 'thehubz-asset-buyout-rights',
  templateUrl: './asset-buyout-rights.component.html',
  styleUrls: ['./asset-buyout-rights.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ClientCoreModule,
    MatExpansionModule,
    AssetSettingsStepperComponent,
    AssetItemBasicComponent,
    BuyoutRightsStandardComponent,
    BuyoutRightsCustomComponent,
  ],
})
export class AssetBuyoutRightsComponent implements OnInit {
  stepperDataObj!: {
    stepperData: StepperModel;
    additionalData: AnyType;
    callFrom: string;
    editable: boolean;
  };
  currentStep!: StepperGroupModel;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
  };
  buyoutRightsConfigs!: BuyoutRightsConfig;
  assetLists: AssetUIModel[] = [];
  brandingKey!: CustomClients;
  /** Table column configuration JSON  */
  columns: TableColumn[] = [];
  deletedBuyoutRightIds: string[] = [];
  deletedBuyoutRightTerritoryObj = new Map<string, string[]>();
  notesColumnName = '';
  CustomClients = CustomClients;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    @Inject(MAT_DIALOG_DATA) private readonly data: BuyoutRightsDialogs,
    private assetsService: AssetsApiService,
    private commonUtilityLib: CommonUtilityLib,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private readonly assetUrlService: AssetsGenerateUrlService,
    private readonly assetUploadService: AssetUploadService,
    private readonly assetCommonService: AssetCommonService,
    private readonly assetsBulkEditApiService: AssetsBulkEditApiService,
    private readonly dialogRef: MatDialogRef<AssetBuyoutRightsComponent, { responseData: AssetUIModel[]; updatedProperties?: string[] }>,
    private assetBuyoutRightsStepperConfig: AssetBuyoutRightsStepperConfigService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    let uncommonTypes: string[] = [];
    this.assetCommonService.unMatchedUsageRight$.subscribe((data) => {
      uncommonTypes = data;
    });
    let alreadyUsedTiles: string[] = this.data.availableTitles ? [...this.data.availableTitles] : [];
    if (uncommonTypes.length > 0) {
      alreadyUsedTiles = [...alreadyUsedTiles, ...uncommonTypes];
    }
    const commonStepperDataObj = {
      additionalData: this.data.index !== undefined ? { usageRightIndex: this.data.index } : undefined,
      callFrom: AssetMetadata.Rights.ASSET_USAGE_RIGHT,
      editable: this.data.editMode || false,
      singleAssetUpdate: this.data.singleAssetUpdate,
    };
    switch (this.brandingKey) {
      case CustomClients.SEAT:
        this.notesColumnName = 'Zusatzinformationen';
        this.stepperDataObj = {
          ...commonStepperDataObj,
          stepperData: this.assetBuyoutRightsStepperConfig.getAssetSeatBuyoutRightsStepperConfig(alreadyUsedTiles, this.data.editMode),
        };
        break;
      case CustomClients.Standard:
        this.stepperDataObj = {
          ...commonStepperDataObj,
          stepperData: this.assetBuyoutRightsStepperConfig.getAssetBuyoutRightsMakeOSStepperConfig(alreadyUsedTiles, this.data.editMode),
        };
        this.columns = BuyoutRightsColumnData.TABLE_MAKEOS_COLUMNS;
        break;
      default:
        this.stepperDataObj = {
          ...commonStepperDataObj,
          stepperData: this.assetBuyoutRightsStepperConfig.getAssetBuyoutRightsStepperConfig(alreadyUsedTiles, this.data.editMode),
        };
        this.columns = BuyoutRightsColumnData.TABLE_DEFAULT_COLUMNS;
        break;
    }
    this.buyoutRightsConfigs = getBuyoutRightsConfig(this.brandingKey);
    this.currentStep = this.stepperDataObj.stepperData.stepper[0];
    this.assetLists = this.data.uploadedAssets;
  }

  async updateAssetBuyout(formValues: AnyType) {
    let usageRightsInfo: AnyType;
    if (this.brandingKey === CustomClients.Standard) {
      usageRightsInfo = this.assetCommonService.constructMakeOSUsageRights(formValues, this.deletedBuyoutRightIds, this.deletedBuyoutRightTerritoryObj);
    } else {
      usageRightsInfo = this.constructUsageRights(formValues);
    }
    if (!usageRightsInfo) {
      this.dialogRef.close();
      return;
    }
    // If single asset update is enabled, edit mode is active, and usage rights info exists,
    // update the usage rights of the asset with the updated usage rights info.
    if (this.data.singleAssetUpdate && this.data.editMode && usageRightsInfo?.usageRights) {
      const objectToUpdate = Object.assign({}, ...usageRightsInfo.usageRights);
      objectToUpdate['_id'] = this.data.buyoutRightEditId;
      this.assetsService.assetsControllerUpdateUsageRight(this.assetLists[0]._id, objectToUpdate).subscribe((asset) => this.emitUpdatedAsset([asset]));
      return;
    }

    // If single asset update is enabled, edit mode is active, and make usage rights info exists,
    // update the usage rights of the asset with the updated usage rights info.
    if (
      this.data.singleAssetUpdate &&
      this.data.editMode &&
      (usageRightsInfo?.makeBuyoutRights || this.deletedBuyoutRightIds?.length || this.deletedBuyoutRightTerritoryObj.size)
    ) {
      const buyoutRightsPayLoadObj = this.assetCommonService.constructMakeOSUsageRightsForUpdates(
        formValues,
        this.assetLists[0],
        this.data.buyoutRightEditId as string,
      );
      buyoutRightsPayLoadObj.deletedBuyoutRightIds = this.deletedBuyoutRightIds;
      buyoutRightsPayLoadObj.deletedBuyoutRightTerritoryIds = Object.fromEntries(this.deletedBuyoutRightTerritoryObj);

      this.assetsService
        .assetsControllerUpdateMakeOSUsageRight(this.assetLists[0]._id, buyoutRightsPayLoadObj)
        .subscribe((asset) => this.emitUpdatedAsset([asset]));
      return;
    }

    // If single asset update is enabled, edit mode is not active, and usage rights info exists,
    // create a new usage right for the asset with the new usage rights info.
    if (this.data.singleAssetUpdate && !this.data.editMode && usageRightsInfo?.usageRights?.[0]) {
      this.assetsService
        .assetsControllerCreateUsageRight(this.assetLists[0]._id, usageRightsInfo.usageRights[0] as UsageRights)
        .subscribe((asset) => this.emitUpdatedAsset([asset]));
      return;
    }

    // If single asset update is enabled, edit mode is not active, and make usage rights info exists,
    // create a new usage right for the asset with the new usage rights info.
    if (this.data.singleAssetUpdate && !this.data.editMode && usageRightsInfo?.makeBuyoutRights?.[0]) {
      this.assetsService
        .assetsControllerCreateMakeOSUsageRight(this.assetLists[0]._id, usageRightsInfo.makeBuyoutRights[0] as MakeBuyoutRights)
        .subscribe((asset) => this.emitUpdatedAsset([asset]));
      return;
    }

    // If multiple assets are being updated, edit mode is active, and usage rights info exists,
    // update the usage rights of the selected assets with the new usage rights info.
    if (!this.data.singleAssetUpdate && this.data.editMode && usageRightsInfo?.usageRights?.[0]) {
      const assetIds = this.assetLists.map((list) => list._id);
      const flatUsageRights = this.assetLists.flatMap((list) => (list ? list.usageRights : [])) as UsageRights[];
      const buyoutRightsTitle = flatUsageRights.find((list) => list._id.toString() === this.data.buyoutRightEditId)?.buyoutRightsTitle;
      const usageRightIds = flatUsageRights.filter((list) => list.buyoutRightsTitle === buyoutRightsTitle).map((list) => list._id.toString());
      const objectToUpdate = Object.assign({}, ...usageRightsInfo.usageRights);
      if (objectToUpdate.buyoutRights) {
        this.removeLicenseRegionKey(objectToUpdate.buyoutRights);
      }
      const updatedProperties = Object.keys(objectToUpdate);
      this.assetsService
        .assetsControllerUpdateManyUsageRight(assetIds, objectToUpdate, usageRightIds)
        .subscribe((asset) => this.emitUpdatedAsset(asset, updatedProperties));
      return;
    }

    // If multiple assets are being updated, edit mode is active, and make usage rights info exists,
    // update the usage rights of the selected assets with the new usage rights info.
    if (
      (!this.data.singleAssetUpdate && this.data.editMode && usageRightsInfo?.makeBuyoutRights?.[0]) ||
      this.deletedBuyoutRightIds?.length ||
      this.deletedBuyoutRightTerritoryObj.size
    ) {
      const buyoutRightsPayLoadObj: BuyoutRightsMultiEditPayload = this.assetCommonService.constructMakeOSUsageRightsForMultiEdit(
        formValues,
        this.assetLists[0],
        this.data.buyoutRightEditId as unknown as string[], // is an array because of multiple assets
      );
      const assetIds = this.assetLists.map((list) => list._id);
      buyoutRightsPayLoadObj.deletedBuyoutRightIds = this.deletedBuyoutRightIds;
      buyoutRightsPayLoadObj.deletedBuyoutRightTerritoryIds = Object.fromEntries(this.deletedBuyoutRightTerritoryObj);
      this.assetsBulkEditApiService
        .assetsBulkEditControllerUpdateMakeBuyout(assetIds, buyoutRightsPayLoadObj)
        .subscribe((asset) => this.emitUpdatedAsset(asset));
      return;
    }

    // If multiple assets are being updated and no single asset update is enabled,
    // update the usage rights of the selected assets with the new usage rights info.
    if (!this.data.singleAssetUpdate) {
      const assetIds: string[] = this.assetLists.map((list) => list._id);
      const updateAssetManyDto: UpdateAssetManyDto = {
        ids: assetIds,
        updateData: usageRightsInfo as AnyType,
      };
      const assets = await this.assetUploadService.updateManyAsset(updateAssetManyDto);
      const responseData = this.assetUrlService.generatePreview(assets);
      this.dialogRef.close({ responseData });
      return;
    }

    // If none of the conditions are met, close the dialog.
    this.dialogRef.close();
  }

  /**
   * Construct usage right
   * @param rawFormValues
   * @returns
   */
  constructUsageRights(rawFormValues: AnyType) {
    const usageRights = this.assetCommonService.transformAndFilterKeys(rawFormValues.usageRightsGeneralInformation, ['notes']);
    const buyoutRightsFormArray = rawFormValues.buyoutRights?.buyoutRightsFormArray;
    let buyoutRights: Partial<BuyoutRights>[] = [];
    if (this.data.editMode && this.data.index !== undefined && this.assetLists[0].usageRights) {
      buyoutRights = buyoutRightsFormArray ? this.assetLists[0].usageRights[this.data.index].buyoutRights || [] : [];
      for (const rights in buyoutRightsFormArray) {
        const valueOnly = this.assetCommonService.transformAndFilterKeys(JSON.parse(JSON.stringify(buyoutRightsFormArray[rights])), []);
        for (const right in buyoutRightsFormArray[rights]) {
          if (buyoutRights[+rights]) buyoutRights[+rights][right as keyof (typeof buyoutRights)[0]] = valueOnly[right];
          else buyoutRights[buyoutRights.length] = { [right as keyof (typeof buyoutRights)[0]]: valueOnly[right] };
        }
      }
    } else {
      for (const rights in buyoutRightsFormArray) {
        buyoutRights.push(this.assetCommonService.transformAndFilterKeys(buyoutRightsFormArray[rights], []));
      }
    }

    if (buyoutRights.length !== 0) usageRights['buyoutRights'] = buyoutRights;
    if (!this.commonUtilityLib.isEmptyObject(usageRights)) return { usageRights: [usageRights] };
    return undefined;
  }

  private removeLicenseRegionKey(objectToUpdate: AnyType[]) {
    objectToUpdate.forEach((obj) => {
      if (obj.licenseRegionKey) delete obj.licenseRegionKey;
    });
  }

  deleteAssetBuyoutRight(event: number) {
    if (this.data.index === undefined) return;
    if (this.assetLists[0]?.usageRights && this.assetLists[0]?.usageRights?.[this.data.index]?.buyoutRights?.length) {
      this.assetLists[0].usageRights[this.data.index].buyoutRights?.splice(event, 1);
    }
    if (this.assetLists[0]?.makeBuyoutRights && this.assetLists[0]?.makeBuyoutRights?.[this.data.index]?.buyoutRights?.length) {
      const deletedBuyoutRightId = (this.assetLists[0].makeBuyoutRights[this.data.index].buyoutRights as AnyType)?.[event]._id;
      this.deletedBuyoutRightIds.push(...(Array.isArray(deletedBuyoutRightId) ? deletedBuyoutRightId : [deletedBuyoutRightId]));
      this.assetLists[0].makeBuyoutRights[this.data.index].buyoutRights?.splice(event, 1);
    }
  }

  handleAssetBuyoutRightTerritoryRemoval(event: { territoryIndex: number; parentRowIndex: number }) {
    if (this.data.index === undefined) return;
    if (this.assetLists[0]?.makeBuyoutRights && this.assetLists[0]?.makeBuyoutRights?.[this.data.index]?.buyoutRights?.length) {
      const currentBuyoutRight = this.assetLists[0].makeBuyoutRights[this.data.index].buyoutRights[event.parentRowIndex] as AnyType;
      if (!currentBuyoutRight) return;
      const removedTerritoryId = currentBuyoutRight.territories?.[event.territoryIndex]?._id;
      if (this.isRemovedTerritoryArray(currentBuyoutRight._id, removedTerritoryId)) {
        this.addValueToBuyoutRightMultiEditTerritoryObj(currentBuyoutRight._id, removedTerritoryId);
      } else {
        this.addValueToBuyoutRightTerritoryObj(currentBuyoutRight._id, removedTerritoryId);
      }
    }
  }

  isRemovedTerritoryArray(currentBuyoutRightId: string | string[], removedTerritoryId: string | string[]): boolean {
    return Array.isArray(removedTerritoryId) && removedTerritoryId.length > 0 && Array.isArray(currentBuyoutRightId) && currentBuyoutRightId.length > 0;
  }

  addValueToBuyoutRightMultiEditTerritoryObj(keys: string[], values: string[]) {
    keys.forEach((key, index) => {
      const mapKey = key;
      const value = values[index];

      if (!this.deletedBuyoutRightTerritoryObj.has(mapKey)) {
        this.deletedBuyoutRightTerritoryObj.set(mapKey, []);
      }

      this.deletedBuyoutRightTerritoryObj.get(mapKey)?.push(value);
    });
  }

  addValueToBuyoutRightTerritoryObj(key: string, value: string) {
    if (!this.deletedBuyoutRightTerritoryObj.has(key)) {
      this.deletedBuyoutRightTerritoryObj.set(key, []);
    }
    this.deletedBuyoutRightTerritoryObj.get(key)?.push(value);
  }

  /**
   * Use with keyvalue pipe to disable automatic keyvalue sorting
   * @returns
   */
  originalOrder = (): number => {
    return 0;
  };

  /**
   * Update changed asset by emitting events
   * @param asset
   */
  emitUpdatedAsset = (asset: Asset[], updatedProperties?: string[]) => {
    if (this.brandingKey === CustomClients.Vorwerk) {
      asset.forEach((asset) => {
        this.assetsService.assetsControllerUpdateStatus(asset._id, Asset.StatusEnum.AwaitingApproval).subscribe();
        asset.status = Asset.StatusEnum.AwaitingApproval;
      });
    }
    const responseData = this.assetUrlService.generatePreview(asset);
    this.assetCommonService.assetDataSubject$.next(responseData[0]);
    this.assetCommonService.openUpdatedPanel$.next(this.data.index || 0);
    this.clientNotificationHandlerService.successNotification($localize`Buyout rights successfully updated`);
    this.dialogRef.close({ responseData, updatedProperties });
  };
}
